import React, { Suspense, useState } from 'react'
import { Stop } from '@commutifi/modes'
import { SelectableCardGrid, CardGridSize, Skeleton } from '@commutifi-fe/ui'
import cx from 'classnames'
import { FormattedMessage } from 'locales/index'
import OtherImg from 'static/icons/other-illustration.svg'
import { getTranslationKeyForStop } from 'utils/modes'
import styles from './styles.module.scss'
import { FormItemInjectedProps } from 'shared/interfaces'

const ClosedIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Closed'))
const ErrandsIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Errands'))
const FoodIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Food'))
const KidsIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Kids'))
const PhysicalActivityIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/PhysicalActivity'))
const MeetingIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Meeting'))

const { ClickableCard } = SelectableCardGrid

interface CommuteStopOption {
  activity: string
  label: React.ReactNode
  img: React.ReactNode
}

interface ActivityCardsSelectorContextValue {
  onSelect: (id: string) => void
}

const cardButtonStyle = { minHeight: '40px' }
export const NoActivityOptionId = 'no-activity'

// Thanks to Kent C. Dodds for the inspiration:
// https://kentcdodds.com/blog/compound-components-with-react-hooks
const ActivityCardsSelectorContext = React.createContext<ActivityCardsSelectorContextValue | null>(null)

interface ActivityCardsSelectorProps extends FormItemInjectedProps<string[]> {
  cardSize?: CardGridSize.Default | CardGridSize.Small
  equalWidth?: boolean
  className?: string
  children?: React.ReactNode
  multiSelect?: boolean
}

function ActivityCardsSelector({
  id,
  onChange,
  cardSize = CardGridSize.Default,
  value,
  equalWidth = true,
  multiSelect = true,
  className,
  children
}: ActivityCardsSelectorProps) {
  const commuteStopOptions: CommuteStopOption[] = [
    {
      activity: Stop.DropOffKids,
      label: <FormattedMessage id={getTranslationKeyForStop(Stop.DropOffKids)} />,
      img: <KidsIllustration height={72} />
    },
    {
      activity: Stop.FoodDrink,
      label: <FormattedMessage id={getTranslationKeyForStop(Stop.FoodDrink)} />,
      img: <FoodIllustration height={72} />
    },
    {
      activity: Stop.PhysicalActivity,
      label: <FormattedMessage id={getTranslationKeyForStop(Stop.PhysicalActivity)} />,
      img: <PhysicalActivityIllustration height={72} />
    },
    {
      activity: Stop.Meetings,
      label: <FormattedMessage id={getTranslationKeyForStop(Stop.Meetings)} />,
      img: <MeetingIllustration height={72} />
    },
    {
      activity: Stop.Errands,
      label: <FormattedMessage id={getTranslationKeyForStop(Stop.Errands)} />,
      img: <ErrandsIllustration height={72} />
    }
  ]

  const [selectedActivity, setSelectedActivity] = useState<string[] | undefined>(undefined)
  const currentActivity = value || selectedActivity

  const handleActivityChange = React.useCallback(
    (activities: string[]) => {
      if (onChange) {
        onChange(activities)
      }
    },
    [onChange]
  )

  const handleSelectActivity = React.useCallback(
    (activities: string | string[]) => {
      // When we select 'None' in the activity options we will have a single string and we want
      // the 'None' option to be the only one selected.
      // Otherwise, when we select other activities we want to make sure to unselect the 'None' option
      const activityArray = Array.isArray(activities)
        ? activities.filter((activity: string) => activity !== NoActivityOptionId)
        : [activities]
      setSelectedActivity(activityArray)
      handleActivityChange(activityArray)
    },
    [handleActivityChange]
  )

  const renderButtonCard = (commuteStopOption: CommuteStopOption) => (
    <ClickableCard
      key={commuteStopOption.activity}
      id={commuteStopOption.activity}
      cover={
        <span className="u-margin--medium u-margin-bottom--none">
          <Suspense fallback={<Skeleton.Image style={{ height: '72px', width: '72px' }} />}>
            {commuteStopOption.img}
          </Suspense>
        </span>
      }
      contentStyle={cardButtonStyle}
      title={commuteStopOption.label}
    />
  )

  const memoValue = React.useMemo(() => ({ onSelect: handleSelectActivity }), [handleSelectActivity])

  return (
    // The only lazy loaded components are the SVGs so we simply wrap the whole component in Suspense
    <ActivityCardsSelectorContext.Provider value={memoValue}>
      <SelectableCardGrid
        id={id}
        multiSelect={multiSelect}
        equalWidth={equalWidth}
        cardSize={cardSize}
        selectedCards={currentActivity}
        initialSelectedCards={currentActivity}
        onChange={handleSelectActivity}
        className={cx(styles['activity-selector'], 'app-activity-selector', className)}
      >
        {commuteStopOptions.map(renderButtonCard)}
        {children}
      </SelectableCardGrid>
    </ActivityCardsSelectorContext.Provider>
  )
}

const useActivityCardsSelectorContext = () => {
  const context = React.useContext(ActivityCardsSelectorContext)
  if (!context) {
    throw new Error(
      'NoActivityOption compound components cannot be rendered outside the ActivityCardsSelector component'
    )
  }
  return context
}

function NoActivityOption() {
  const { onSelect } = useActivityCardsSelectorContext()
  const handleClick = () => {
    onSelect(NoActivityOptionId)
  }

  return (
    <ClickableCard
      id={NoActivityOptionId}
      cover={
        <span className="u-margin--medium u-margin-bottom--none">
          <Suspense fallback={<Skeleton.Image style={{ height: '72px', width: '72px' }} />}>
            <ClosedIllustration height={72} />
          </Suspense>
        </span>
      }
      title={<FormattedMessage id="global.activities.none" />}
      contentStyle={cardButtonStyle}
      onClick={handleClick}
    />
  )
}

function OtherActivityOption() {
  const { onSelect } = useActivityCardsSelectorContext()
  const handleClick = () => {
    onSelect(Stop.Other)
  }

  return (
    <ClickableCard
      id={Stop.Other}
      cover={
        <span className="u-margin--medium u-margin-bottom--none">
          <img src={OtherImg} alt="Other Activity Illustration" />
        </span>
      }
      title={<FormattedMessage id="global.activities.extra" />}
      contentStyle={cardButtonStyle}
      onClick={handleClick}
    />
  )
}

ActivityCardsSelector.NoActivityOption = NoActivityOption
ActivityCardsSelector.Other = OtherActivityOption
export default ActivityCardsSelector
